import { useCallback, useMemo } from 'react';
import { useInvestment } from '../queries';
import { getInvestmentFormDefaultValues } from '../util';
import { SubmitHandler, useForm } from 'react-hook-form';
import { InvestmentFormSubmitData, InvestmentFormValues } from '../types';
import { useSubmitOnChange } from '../../UI';

export interface UseInvestmentFormArgs {
  householdID: string;
  saveOnChange?: boolean;
  investmentID?: string | null;
  defaultValueOverrides?: Partial<InvestmentFormValues>;
  handleSubmit?: (data: InvestmentFormSubmitData) => void;
}

/**
 * 
 * @deprecated Use useCreateInvestmentForm from  packages/elements/src/UI/components/InvestmentForm/hooks/useCreateInvestmentForm.ts
 * or useEditInvestmentForm from  packages/elements/src/UI/components/InvestmentForm/hooks/useEditInvestmentForm.ts
 * instead
 */
export const useInvestmentForm = ({
  householdID,
  handleSubmit,
  investmentID,
  saveOnChange,
  defaultValueOverrides,
}: UseInvestmentFormArgs) => {
  const { investment, investmentQuery } = useInvestment({ householdID, investmentID });
  // Memoize so the `values` object is referentially stable across renders. Passing a fresh
  // object every render makes react-hook-form re-run its reset-check each render; combined with
  // the income-source dropdown writing incomeSourceID back, that oscillates into an infinite
  // render loop for edited income-source-linked investments (ELE-850).
  const values = useMemo(
    () => getInvestmentFormDefaultValues(investment, defaultValueOverrides),
    [investment, defaultValueOverrides]
  );
  const formMethods = useForm<InvestmentFormValues>({
    // @ts-expect-error TODO(ELE-668): getInvestmentFormDefaultValues' return type doesn't line up with InvestmentFormValues
    values,
    // Validate on submit only — not on every keystroke after the first save attempt. The
    // contribution pairs (income+percentage, amount+frequency) are cross-field, so on-change
    // revalidation nags "fill the other field" the instant you engage one side. Fields clear their
    // own errors on edit (see the contribution inputs), and everything re-checks on the next save.
    reValidateMode: 'onSubmit',
  });
  const isLoadingFormData = investmentQuery.isLoading;
  const formReady = !isLoadingFormData && formMethods.formState.defaultValues;

  const onSubmit: SubmitHandler<InvestmentFormValues> = useCallback(
    (formValues) => {
      handleSubmit?.({ formValues, householdID, changeToken: investment?.changeToken, investmentID });
    },
    [handleSubmit, householdID, investment?.changeToken, investmentID]
  );

  useSubmitOnChange({
    onChange: onSubmit,
    watch: formMethods.watch,
    handleSubmit: formMethods.handleSubmit,
    enabled: saveOnChange && !!investment,
  });

  return { formMethods, onSubmit, formReady, isLoadingFormData, investment, householdID };
};
